import { useEffect, useState } from "react";
import { Button } from "@/modern/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/modern/components/ui/tooltip";
import { LinkCompanyCamHeaderButton } from "@/react/components/entityViews/files/components/LinkCompanyCamHeaderButton";
import { CompanyCamProjectStatusLink } from "@/react/components/entityViews/files/components/CompanyCamProjectStatusLink";
import { useEntityFileContext } from "@/react/components/entityViews/files/context/EntityFileContext";
import { useDebounce } from "@/react/hooks/useDebounce";
import { cn } from "@/modern/utils";
import {
  SURFACE_CONTROL_CLS,
  SURFACE_ICON_BTN_CLS,
  SURFACE_SEARCH_CLS,
} from "@/react/components/entityViews/files/constants/surfaceChrome";
import {
  Download,
  Grid3x3,
  List,
  Loader2,
  Search,
  SquareX,
  Trash2,
  Upload,
  X,
  ZoomIn,
  ZoomOut,
} from "lucide-react";
import { DisplayPopover } from "./DisplayPopover";
import { FiltersPopover } from "./FiltersPopover";
import { SortPopover } from "./SortPopover";
import {
  DEFAULT_FILE_BROWSER_FILTERS,
  type FileBrowserFilterState,
} from "./types";

type ViewMode = "grid" | "list";

export type FileGridToolbarVariant = "default" | "compact";

/** When set with selectedCount > 0, the command rail morphs into selection mode. */
export interface FileGridToolbarSelection {
  selectedCount: number;
  onClear: () => void;
  onDownload: () => void;
  onDelete: () => void;
  isBusy?: boolean;
}

interface FileGridToolbarProps {
  filters: FileBrowserFilterState;
  onFiltersChange: (next: FileBrowserFilterState) => void;
  /** Commit debounced search into provider filters (avoids stale closures). */
  onSearchChange: (search: string) => void;
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
  zoomLevel: number;
  zoomMax: number;
  zoomLabel?: string;
  onZoomIn: () => void;
  onZoomOut: () => void;
  isUploading: boolean;
  onUpload: () => void;
  uploadAcceptLabel: string;
  uploadRef: React.RefObject<HTMLInputElement>;
  accept: string;
  onUploadChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  /**
   * `compact` — Task Media / Documents manage: one command row, display menu,
   * status lived in the dialog header. Other surfaces keep `default`.
   */
  variant?: FileGridToolbarVariant;
  /** Media multi-select: replaces browse chrome in-place (no extra header band). */
  selection?: FileGridToolbarSelection | null;
}

export function FileGridToolbar({
  filters,
  onFiltersChange,
  onSearchChange,
  viewMode,
  onViewModeChange,
  zoomLevel,
  zoomMax,
  zoomLabel = "Size",
  onZoomIn,
  onZoomOut,
  isUploading,
  onUpload,
  uploadAcceptLabel,
  uploadRef,
  accept,
  onUploadChange,
  variant = "default",
  selection = null,
}: FileGridToolbarProps) {
  const { totalCount } = useEntityFileContext();
  const [searchDraft, setSearchDraft] = useState(filters.search);
  const debouncedSearch = useDebounce(searchDraft, 300) as string;
  const isCompactChrome = variant === "compact";
  const isSelectionMode = Boolean(selection && selection.selectedCount > 0);
  const selectionBusy = selection?.isBusy ?? false;
  const searchPlaceholder =
    uploadAcceptLabel === "documents"
      ? "Search documents…"
      : uploadAcceptLabel === "media"
        ? "Search photos…"
        : "Search description or filename…";

  useEffect(() => {
    setSearchDraft(filters.search);
  }, [filters.search]);

  useEffect(() => {
    if (debouncedSearch === filters.search) return;
    onSearchChange(debouncedSearch);
  }, [debouncedSearch, filters.search, onSearchChange]);

  const patch = (partial: Partial<FileBrowserFilterState>) => {
    onFiltersChange({ ...filters, ...partial });
  };

  const resetMenuFilters = () => {
    patch({
      fileTypeTab: DEFAULT_FILE_BROWSER_FILTERS.fileTypeTab,
      source: DEFAULT_FILE_BROWSER_FILTERS.source,
      datePreset: DEFAULT_FILE_BROWSER_FILTERS.datePreset,
    });
  };

  const selectionCountLabel =
    selection && selection.selectedCount === 1
      ? "1 selected"
      : `${(selection?.selectedCount ?? 0).toLocaleString()} selected`;

  return (
    <div className={cn("flex shrink-0 flex-col", isCompactChrome ? "mb-3 mt-2 gap-0" : "my-4 gap-2")}>
      {isSelectionMode && selection ? (
        <div
          className="flex flex-nowrap items-center gap-2"
          role="toolbar"
          aria-label="Selected photos"
        >
          <div className="flex min-w-0 flex-1 items-center">
            <span className="min-w-0 truncate text-sm font-normal tabular-nums text-foreground">
              {selectionCountLabel}
            </span>
          </div>
          <div className="ml-auto flex shrink-0 items-center gap-2">
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={selection.onClear}
                  disabled={selectionBusy}
                  aria-label="Deselect all"
                  className={cn(
                    SURFACE_ICON_BTN_CLS,
                    "inline-flex items-center justify-center disabled:opacity-40",
                  )}
                >
                  <SquareX className="size-3.5" aria-hidden />
                </button>
              </TooltipTrigger>
              <TooltipContent side="bottom">Deselect all</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={selection.onDownload}
                  disabled={selectionBusy}
                  aria-label="Download selected"
                  className={cn(
                    SURFACE_ICON_BTN_CLS,
                    "inline-flex items-center justify-center disabled:opacity-40",
                  )}
                >
                  <Download className="size-3.5" aria-hidden />
                </button>
              </TooltipTrigger>
              <TooltipContent side="bottom">Download</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={selection.onDelete}
                  disabled={selectionBusy}
                  aria-label="Delete selected"
                  className={cn(
                    SURFACE_ICON_BTN_CLS,
                    "inline-flex items-center justify-center text-destructive hover:text-destructive disabled:opacity-40",
                  )}
                >
                  <Trash2 className="size-3.5" aria-hidden />
                </button>
              </TooltipTrigger>
              <TooltipContent side="bottom">Delete</TooltipContent>
            </Tooltip>
          </div>
        </div>
      ) : (
      <div className="flex flex-nowrap items-center gap-2">
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <div className="relative min-w-0 max-w-md flex-1">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground/60"
              aria-hidden
            />
            <input
              type="text"
              value={searchDraft}
              onChange={(e) => setSearchDraft(e.target.value)}
              placeholder={isCompactChrome ? searchPlaceholder : "Search description or filename…"}
              aria-label={isCompactChrome ? searchPlaceholder.replace(/…$/, "") : "Search description or filename"}
              className={SURFACE_SEARCH_CLS}
            />
            {searchDraft ? (
              <button
                type="button"
                onClick={() => {
                  setSearchDraft("");
                  onSearchChange("");
                }}
                aria-label="Clear search"
                className="absolute right-1.5 top-1/2 flex size-6 -translate-y-1/2 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                <X className="size-3.5" aria-hidden />
              </button>
            ) : null}
          </div>

          <FiltersPopover
            filters={filters}
            onChange={patch}
            onResetFilters={resetMenuFilters}
          />
          <SortPopover ordering={filters.ordering} onChange={patch} />
        </div>

        <div className="ml-auto flex shrink-0 items-center gap-2">
          {isCompactChrome ? (
            <DisplayPopover
              viewMode={viewMode}
              onViewModeChange={onViewModeChange}
              zoomLevel={zoomLevel}
              zoomMax={zoomMax}
              zoomLabel={zoomLabel}
              onZoomIn={onZoomIn}
              onZoomOut={onZoomOut}
            />
          ) : (
            <>
              {viewMode === "grid" ? (
                <div className="inline-flex items-center gap-1" role="group" aria-label="Zoom">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="inline-flex">
                        <button
                          type="button"
                          onClick={onZoomOut}
                          disabled={zoomLevel === 0}
                          aria-label="Zoom out"
                          className={cn(
                            SURFACE_ICON_BTN_CLS,
                            "inline-flex items-center justify-center disabled:opacity-40",
                          )}
                        >
                          <ZoomOut className="size-3.5" aria-hidden />
                        </button>
                      </span>
                    </TooltipTrigger>
                    <TooltipContent side="bottom">Zoom out</TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="inline-flex">
                        <button
                          type="button"
                          onClick={onZoomIn}
                          disabled={zoomLevel === zoomMax}
                          aria-label="Zoom in"
                          className={cn(
                            SURFACE_ICON_BTN_CLS,
                            "inline-flex items-center justify-center disabled:opacity-40",
                          )}
                        >
                          <ZoomIn className="size-3.5" aria-hidden />
                        </button>
                      </span>
                    </TooltipTrigger>
                    <TooltipContent side="bottom">Zoom in</TooltipContent>
                  </Tooltip>
                </div>
              ) : null}

              <div
                role="group"
                aria-label="View mode"
                className={cn(
                  "inline-flex items-center gap-0.5 rounded-full p-0.5",
                  SURFACE_CONTROL_CLS,
                )}
              >
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      onClick={() => onViewModeChange("list")}
                      aria-pressed={viewMode === "list"}
                      aria-label="List view"
                      className={cn(
                        "inline-flex size-8 items-center justify-center rounded-full transition-[transform,background-color] duration-150 ease-out active:scale-[0.97]",
                        viewMode === "list"
                          ? "bg-background text-foreground shadow-sm"
                          : "text-muted-foreground hover:text-foreground",
                      )}
                    >
                      <List className="size-3.5" aria-hidden />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">List view</TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      onClick={() => onViewModeChange("grid")}
                      aria-pressed={viewMode === "grid"}
                      aria-label="Grid view"
                      className={cn(
                        "inline-flex size-8 items-center justify-center rounded-full transition-[transform,background-color] duration-150 ease-out active:scale-[0.97]",
                        viewMode === "grid"
                          ? "bg-background text-foreground shadow-sm"
                          : "text-muted-foreground hover:text-foreground",
                      )}
                    >
                      <Grid3x3 className="size-3.5" aria-hidden />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">Grid view</TooltipContent>
                </Tooltip>
              </div>
            </>
          )}

          <div
            className="inline-flex items-center gap-2"
            role="group"
            aria-label="Upload and CompanyCam"
          >
            <LinkCompanyCamHeaderButton
              className={cn(
                SURFACE_ICON_BTN_CLS,
                "inline-flex items-center justify-center shadow-none",
              )}
            />
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  size="default"
                  className="h-9 gap-1.5 rounded-full px-4 font-normal"
                  onClick={onUpload}
                  disabled={isUploading}
                >
                  {isUploading ? (
                    <Loader2 className="size-3.5 animate-spin" aria-hidden />
                  ) : (
                    <Upload className="size-3.5" aria-hidden />
                  )}
                  Upload
                </Button>
              </TooltipTrigger>
              <TooltipContent>Upload {uploadAcceptLabel}</TooltipContent>
            </Tooltip>
          </div>

          <input
            ref={uploadRef}
            type="file"
            className="hidden"
            multiple
            accept={accept}
            onChange={onUploadChange}
          />
        </div>
      </div>
      )}

      {!isCompactChrome ? (
        <div className="flex max-w-md flex-wrap items-center gap-x-3 gap-y-1 px-1 text-sm font-normal text-muted-foreground">
          <span className="tabular-nums">
            Showing {totalCount.toLocaleString()} {totalCount === 1 ? "file" : "files"}
          </span>
          <CompanyCamProjectStatusLink />
        </div>
      ) : null}
    </div>
  );
}
